/**
 * Builds the Pay Now UI DOM, styled to match the JM Search paymentDetailsModal.
 * Returns a plain HTMLElement — the caller decides where to mount it (modal, inline section, etc.).
 *
 * Usage in index.js (openAmpPayNow):
 *   import { buildPayNowContent } from './helpers/pay-now-ui.js';
 *   const el = buildPayNowContent(buyer, campaign);
 *   document.getElementById('paymentDetailsModal').querySelector('.modal-content').appendChild(el);
 *
 * @param {object} buyer - Participant object with: userid, fname, uname, ampBuyerMaxDollars, signDate, tag
 * @returns {HTMLElement}
 */
export function buildPayNowContent(buyer) {
	const amount = Number(buyer.ampBuyerMaxDollars || 0);

	const fmt = (n) =>
		n != null ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
	const fmtDate = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

	const root = document.createElement('div');

	// ── Header row ────────────────────────────────────────────────────────────
	const headerRow = document.createElement('div');
	headerRow.className = 'row mt-0 mb-2';
	const headerInner = document.createElement('div');
	headerInner.className = 'col s12 mb-0';

	const h2 = document.createElement('h2');
	h2.className = 'text-2xl font-bold mt-0 mb-0';
	h2.textContent = 'Payment Details';
	headerInner.appendChild(h2);

	headerRow.appendChild(headerInner);

	const headerDivider = document.createElement('div');
	headerDivider.className = 'border-t-1 mb-4';

	// ── Amount row ────────────────────────────────────────────────────────────
	const amountRow = document.createElement('div');
	amountRow.className = 'row mt-0 mb-4';
	const amountCol = document.createElement('div');
	amountCol.className = 'col s12 mb-0';

	const thankYouInfo = document.createElement('p');
	thankYouInfo.className = 'text-sm mt-0 mb-2';
	thankYouInfo.textContent = 'Thank you for participating in Alera Marketplace (AMP) 2026.';

	const sharesInfo = document.createElement('div');
	sharesInfo.className = 'mt-0 mb-3';

	const sharesLabel = document.createElement('p');
	sharesLabel.className = 'text-xs text-secondary mt-0 mb-1';
	sharesLabel.textContent = 'You will be purchasing shares equal in value to this payment amount:';

	const sharesAmount = document.createElement('p');
	sharesAmount.className = 'text-lg font-bold mt-0 mb-0';
	sharesAmount.textContent = fmt(amount);

	sharesInfo.appendChild(sharesLabel);
	sharesInfo.appendChild(sharesAmount);

	const amountInfo = document.createElement('p');
	amountInfo.className = 'text-xs text-secondary mt-1 mb-0';
	amountInfo.textContent =
		'This amount will be withdrawn via ACH from your authorized bank account once submitted. ACH transfers typically take 4-5 business days to process.';

	amountCol.appendChild(thankYouInfo);
	amountCol.appendChild(sharesInfo);
	amountCol.appendChild(amountInfo);
	amountRow.appendChild(amountCol);

	// ── Button row ────────────────────────────────────────────────────────────
	const btnRow = document.createElement('div');
	btnRow.className = 'row mt-0 mb-4';
	const btnInner = document.createElement('div');
	btnInner.className = 'col s12 mb-0';

	const btnInfo = document.createElement('p');
	btnInfo.className = 'text-xs text-secondary mt-0 mb-2';
	btnInfo.textContent = 'Click Pay Now to proceed. You will be prompted for SMS multi-factor authentication.';

	const payBtn = document.createElement('button');
	payBtn.className = 'waves-effect waves-light btn btn-large mt-0 mb-0 disabled amp-pay-now-btn';
	payBtn.style.width = '100%';
	payBtn.textContent = 'PAY NOW';
	payBtn.disabled = true;

	btnInner.appendChild(btnInfo);
	btnInner.appendChild(payBtn);
	btnRow.appendChild(btnInner);

	const footerDivider = document.createElement('div');
	footerDivider.className = 'border-t-1 mb-3';

	// ── Footer ────────────────────────────────────────────────────────────────
	const footer = document.createElement('div');
	footer.className = 'justify-between flex items-center';

	const footerSigned = document.createElement('p');
	footerSigned.className = 'text-xs mb-0 ml-4';
	footerSigned.textContent = 'Commitment Signed: ' + fmtDate(buyer.signDate);

	const footerPrivate = document.createElement('p');
	footerPrivate.className = 'text-xs text-secondary mb-0 mr-4';
	footerPrivate.textContent = 'Private & Confidential';

	footer.appendChild(footerSigned);
	footer.appendChild(footerPrivate);

	// ── Assemble ──────────────────────────────────────────────────────────────
	root.appendChild(headerRow);
	root.appendChild(headerDivider);
	root.appendChild(amountRow);
	root.appendChild(btnRow);
	root.appendChild(footerDivider);
	root.appendChild(footer);

	return root;
}

/**
 * Builds a payment status view from get-payments response records.
 * @param {object[]} payments - Array of payment records from /auth/get-payments
 * @returns {HTMLElement}
 */
export function buildPaymentStatusContent(payments) {
	const fmt = (n) =>
		n != null ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
	const fmtDate = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
	const statusColor = (s) => {
		if (!s) return '#888';
		const u = s.toUpperCase();
		if (['PAID', 'COMPLETED', 'CHECK SENT', 'WIRE SENT'].includes(u) || u.startsWith('PROCESSED')) return 'green';
		if (u === 'PENDING' || u === 'SENT') return '#e6a817';
		if (u === 'FAILED') return '#c62828';
		return '#888';
	};

	const root = document.createElement('div');

	// ── Header ────────────────────────────────────────────────────────────────
	const headerRow = document.createElement('div');
	headerRow.className = 'row mt-0 mb-2';
	const headerInner = document.createElement('div');
	headerInner.className = 'col s12 mb-0';
	const h2 = document.createElement('h2');
	h2.className = 'text-2xl font-bold mt-0 mb-0';
	h2.textContent = 'Payment Status';
	headerInner.appendChild(h2);
	headerRow.appendChild(headerInner);

	// ── Table ─────────────────────────────────────────────────────────────────
	const tableRow = document.createElement('div');
	tableRow.className = 'row';
	const tableWrap = document.createElement('div');
	tableWrap.className = 'table-wrap ml-1 mt-6 mb-0';

	if (!payments.length) {
		const empty = document.createElement('p');
		empty.className = 'text-sm text-secondary center-align py-4';
		empty.textContent = 'No payment records found.';
		tableWrap.appendChild(empty);
	} else {
		const table = document.createElement('table');
		table.className = 'highlight table-content';
		table.style.tableLayout = 'fixed';

		const colgroup = document.createElement('colgroup');
		['50%', '25%', '25%'].forEach((width) => {
			const col = document.createElement('col');
			col.style.width = width;
			colgroup.appendChild(col);
		});
		table.appendChild(colgroup);

		const thead = document.createElement('thead');
		const htr = document.createElement('tr');
		['Date Payment Initiated', 'Amount', 'Status'].forEach((text, i) => {
			const th = document.createElement('th');
			if (i > 0) th.className = 'center-align';
			th.textContent = text;
			htr.appendChild(th);
		});
		thead.appendChild(htr);

		const tbody = document.createElement('tbody');
		payments.forEach((p) => {
			const tr = document.createElement('tr');
			const tdDate = document.createElement('td');
			tdDate.className = 'text-sm';
			tdDate.textContent = fmtDate(p.Id);
			const tdAmt = document.createElement('td');
			tdAmt.className = 'text-sm center-align';
			tdAmt.textContent = fmt(p.amount);
			const tdStatus = document.createElement('td');
			tdStatus.className = 'text-sm center-align';
			const badge = document.createElement('span');
			badge.style.color = statusColor(p.achStatus);
			badge.style.fontWeight = '600';
			badge.textContent = p.achStatus || 'PENDING';
			tdStatus.appendChild(badge);
			tr.appendChild(tdDate);
			tr.appendChild(tdAmt);
			tr.appendChild(tdStatus);
			tbody.appendChild(tr);
		});

		table.appendChild(thead);
		table.appendChild(tbody);
		tableWrap.appendChild(table);
	}

	tableRow.appendChild(tableWrap);

	const achInfo = document.createElement('p');
	achInfo.className = 'text-xs text-secondary mt-3 mb-0';
	achInfo.textContent = 'ACH transfers typically take 4-5 business days to process.';

	const processedInfo = document.createElement('p');
	processedInfo.className = 'text-xs text-secondary mt-1 mb-0';
	processedInfo.textContent = 'Your payment status will be marked "Processed" once it has completed.';

	root.appendChild(headerRow);
	root.appendChild(tableRow);
	root.appendChild(achInfo);
	root.appendChild(processedInfo);
	return root;
}
